import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { GraphQLFormattedError } from 'graphql';
import {
  AccessComponentEnum,
  AccessLevelEnum,
  FilterGroupingEnum,
  SectorCreateInputObject,
  SectorUpdateInputObject,
  Site,
  User,
  UserFilterInputObject,
  UserRoleEnum,
  useSectorCreateMutation,
  useSectorDestroyMutation,
  useSectorUpdateMutation,
} from 'graphql-common';
import useSectorQueryHook, {
  UseSectorQueryHookResult,
} from 'utils/fetch/hooks/useSectorQueryHook';
import { Values } from '@lib/interfaces/form';
import { showGraphQLErrors, getGraphQLErrors } from '@lib/utils/graphQLErrors';
import toast from '@lib/components/Toast/Toast';
import { APP_URLS } from 'constants/urls';
import getDefaultValuesFromQueryData from 'routes/Sectors/Sector/utils/getDefaultValuesFromQueryData';
import useUsersQueryHook from 'utils/fetch/hooks/useUsersQueryHook';
import useSitesQueryHook from 'utils/fetch/hooks/useSitesQueryHook';
import { GetUrlParams } from '@lib/enums/urls';
import {
  Modals,
  UseModalManagementResult,
} from '@lib/hooks/useModalManagement';
import { SectorViewMode } from 'routes/Sectors/Sector/enums';
import { ListQueryHookResult } from 'utils/fetch/types';
import usePaginationParams from '@lib/hooks/usePaginationParams';
import {
  UseBulkSelectResult,
  useTableItemsBulkSelect,
} from '@lib/hooks/useTableItemsBulkSelect';
import useOutletContext, { ContextProps } from 'hooks/useOutletContext';
import getPreparedDataFromValues from './getPreparedDataFromValues';

export interface UseActionsResult {
  destroyLoading: boolean;
  graphQLErrors?: readonly GraphQLFormattedError[];
  isUsersManagementLocked: boolean;
  isViewOnly: boolean;
  loading: boolean;
  onCancel: () => void;
  onConfirmedDelete: () => void;
  onDeleteHandler: () => void;
  onEditHandler: () => void;
  onSubmitForm: (values: Values) => void;
  onViewHandler: () => void;
  onViewSectorHandler: (id: string) => void;
  onViewSiteHandler: (id: string) => void;
  onViewTeamHandler: (id: string) => void;
  sectorQueryHookResult: UseSectorQueryHookResult;
  sitesQueryHookResult: ListQueryHookResult<Site>;
  useUsersBulkSelectResult: UseBulkSelectResult<User>;
  usersQueryHookResult: ListQueryHookResult<User>;
}

export default function useSectorActions({
  id,
  mode,
  useModalManagementResult,
}: {
  id: string;
  pathname: string;
  search: string;
  mode: SectorViewMode;
  useModalManagementResult: UseModalManagementResult;
}): UseActionsResult {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const useUsersBulkSelectResult = useTableItemsBulkSelect<User>();
  const [paginationParams] = usePaginationParams();
  const { closeModal, openModal } = useModalManagementResult;

  const { getUserAccessByComponent }: ContextProps = useOutletContext();
  const isViewOnly =
    getUserAccessByComponent(AccessComponentEnum.SitesManagement) ===
    AccessLevelEnum.Read;
  const isUsersManagementLocked =
    getUserAccessByComponent(AccessComponentEnum.UsersManagement) ===
    AccessLevelEnum.LimitedRead;

  // Queries
  const sectorQueryHookResult = useSectorQueryHook({
    id,
  });
  const { loading: dataLoading, data } = sectorQueryHookResult;

  // Users for view
  const additionalFilters: UserFilterInputObject[] = [
    {
      memberAssignedSectorsId: {
        grouping: FilterGroupingEnum.Or,
        predicate: { eq: id },
        filterGroup: 'fullAdminsPlusMemberships',
      },
    },
  ];
  if (mode === SectorViewMode.Users) {
    additionalFilters.push({
      role: {
        grouping: FilterGroupingEnum.Or,
        predicate: { eq: UserRoleEnum.FullAdmin },
        filterGroup: 'fullAdminsPlusMemberships',
      },
    });
  }
  const usersQueryHookResult = useUsersQueryHook({
    paginationParams:
      mode === SectorViewMode.Users
        ? paginationParams
        : {
            [GetUrlParams.Page]: '1',
            [GetUrlParams.PerPage]: '4',
          },
    skip: !(mode === SectorViewMode.Users),
    skipGetAllIds: !(mode === SectorViewMode.Edit),
    additionalFilters,
    includeTeamAccesses: mode === SectorViewMode.Users,
    includeShortData: true,
  });

  // Sites for view
  const sitesQueryHookResult = useSitesQueryHook({
    paginationParams:
      mode === SectorViewMode.Sites
        ? paginationParams
        : {
            [GetUrlParams.Page]: '1',
            [GetUrlParams.PerPage]: '4',
          },
    skip: !(mode === SectorViewMode.Sites),
    skipGetAllIds: !(mode === SectorViewMode.Edit),
    additionalFilters: [
      {
        sectorId: {
          grouping: FilterGroupingEnum.Or,
          predicate: { eq: id },
        },
      },
    ],
  });

  // Mutations
  const [createMutation, createMutationResult] = useSectorCreateMutation();
  const [updateMutation, updateMutationResult] = useSectorUpdateMutation();
  const [destroyMutation, destroyMutationResult] = useSectorDestroyMutation({
    onCompleted: () => {
      closeModal(Modals.DestroyModal);
      toast({ content: t('destroy-sector-success') });
      navigate(APP_URLS.dashboard.sectors.index.getPathWithQuery());
    },
    onError: (err) => showGraphQLErrors(err, t),
  });

  // Actions
  const onSubmitForm = (values: Values) => {
    const attributes = getPreparedDataFromValues({
      values,
      prevValues: getDefaultValuesFromQueryData(
        data,
        usersQueryHookResult.allIds,
        sitesQueryHookResult.allIds,
      ),
    }) as SectorUpdateInputObject;
    if (mode === SectorViewMode.Edit) {
      updateMutation({
        variables: {
          id,
          attributes,
        },
        onCompleted: () => {
          toast({ content: t('update-sector-success') });
          navigate(
            APP_URLS.dashboard.sectors.view.getDynamicPath({
              pathParts: { id },
            }),
          );
        },
        onError: (error) => showGraphQLErrors(error, t),
      });
    } else if (mode === SectorViewMode.Create) {
      createMutation({
        variables: {
          attributes: getPreparedDataFromValues({
            values,
            prevValues: getDefaultValuesFromQueryData(data),
          }) as SectorCreateInputObject,
        },
        onCompleted: (res) => {
          toast({ content: t('create-sector-success') });
          const newId = res.data?.id;
          navigate(
            newId
              ? APP_URLS.dashboard.sectors.view.getDynamicPath({
                  pathParts: { id: newId },
                })
              : APP_URLS.dashboard.sectors.index.getPathWithQuery(),
          );
        },
        onError: (error) => showGraphQLErrors(error, t),
      });
    }
  };

  const onCancel = () => {
    navigate(
      data?.id
        ? APP_URLS.dashboard.sectors.view.getDynamicPath({
            pathParts: { id: data.id },
          })
        : APP_URLS.dashboard.sectors.index.getPathWithQuery(),
    );
  };

  const onEditHandler = () => {
    if (data?.id) {
      navigate(
        APP_URLS.dashboard.sectors.edit.getDynamicPath({
          pathParts: { id: data.id },
        }),
      );
    }
  };

  const onViewHandler = () => {
    if (data?.id) {
      navigate(
        APP_URLS.dashboard.sectors.view.getDynamicPath({
          pathParts: { id: data.id },
        }),
      );
    }
  };

  const onDeleteHandler = () => {
    openModal(Modals.DestroyModal);
  };

  const onConfirmedDelete = () => {
    if (id) {
      destroyMutation({
        variables: {
          id,
        },
      });
    }
  };

  const onViewTeamHandler = (teamId: string) => {
    navigate(
      APP_URLS.dashboard.teams.view.getDynamicPath({
        pathParts: { id: teamId },
      }),
    );
  };

  const onViewSiteHandler = (siteId: string) => {
    navigate(
      APP_URLS.dashboard.sites.view.getDynamicPath({
        pathParts: { id: siteId },
      }),
    );
  };

  const onViewSectorHandler = (sectorId: string) => {
    navigate(
      APP_URLS.dashboard.sectors.view.getDynamicPath({
        pathParts: { id: sectorId },
      }),
    );
  };

  const loading =
    dataLoading || createMutationResult.loading || updateMutationResult.loading;
  const graphQLErrors =
    getGraphQLErrors(createMutationResult?.error) ||
    getGraphQLErrors(updateMutationResult?.error);

  return {
    destroyLoading: destroyMutationResult.loading,
    graphQLErrors,
    isUsersManagementLocked,
    isViewOnly,
    loading,
    onCancel,
    onConfirmedDelete,
    onDeleteHandler,
    onEditHandler,
    onSubmitForm,
    onViewHandler,
    onViewSectorHandler,
    onViewSiteHandler,
    onViewTeamHandler,
    sectorQueryHookResult,
    sitesQueryHookResult,
    useUsersBulkSelectResult,
    usersQueryHookResult,
  };
}
